import { minutes, money, type ISODateTime, type TaskId } from '../domain/branded.js';
import type {
  Baseline,
  BaselineAssignmentSnapshot,
  BaselineBudgetSnapshot,
  BaselineNumber,
  BaselineTaskSnapshot,
} from '../domain/baseline.js';
import { EntityNotFoundError } from '../domain/errors.js';
import type { Project } from '../domain/project.js';
import type { Task } from '../domain/task.js';
import { resolveCalendar, workingMinutesBetween, type ResolvedCalendar } from './calendar-engine.js';
import { computeCost } from './cost.js';

export interface SaveBaselineOptions {
  readonly number: BaselineNumber;
  readonly name?: string;
  readonly savedAt: ISODateTime;
  /** 只存這些任務（與其子任務）；未提供代表整個專案。 */
  readonly onlyTaskIds?: readonly TaskId[];
  /** 是否將子任務彙總回上層摘要。 */
  readonly rollupToSummary?: boolean;
}

/**
 * 計算選取範圍（`onlyTaskIds` 及其所有子孫任務；`rollupToSummary` 時再加回其祖先鏈）。
 * `onlyTaskIds` 未提供時回傳 `null`，代表整個專案都在範圍內。
 */
function resolveScope(
  project: Project,
  options: SaveBaselineOptions,
): ReadonlySet<string> | null {
  if (options.onlyTaskIds === undefined) return null;

  const taskById = new Map<string, Task>(project.tasks.map((t) => [t.id as string, t]));
  const childrenByParentId = new Map<string, Task[]>();
  for (const task of project.tasks) {
    if (task.parentId === null) continue;
    const key = task.parentId as string;
    const list = childrenByParentId.get(key) ?? [];
    list.push(task);
    childrenByParentId.set(key, list);
  }

  const scope = new Set<string>();
  const addWithDescendants = (id: string): void => {
    if (scope.has(id)) return;
    scope.add(id);
    for (const child of childrenByParentId.get(id) ?? []) {
      addWithDescendants(child.id as string);
    }
  };
  for (const id of options.onlyTaskIds) addWithDescendants(id as string);

  if (options.rollupToSummary === true) {
    // 把選取任務的祖先鏈也納入範圍，讓摘要任務在部分擷取時仍有自己的基準線快照。
    for (const id of [...scope]) {
      let parentId = taskById.get(id)?.parentId ?? null;
      while (parentId !== null) {
        const parentKey = parentId as string;
        if (scope.has(parentKey)) break;
        scope.add(parentKey);
        parentId = taskById.get(parentKey)?.parentId ?? null;
      }
    }
  }

  return scope;
}

/**
 * 擷取目前排程為基準線快照。
 *
 * - 葉節點（非摘要）任務：start/finish/work/durationMinutes 直接取自 `Task` 目前欄位；
 *   cost/fixedCost 透過 `computeCost()` 取得，確保與成本引擎口徑一致。
 * - 摘要任務：**由本次實際擷取到的子任務快照彙總得出**，不是照抄 `computeCost()`
 *   的 rollup（那個 rollup 是對「全專案」算的，會把沒被選進 `onlyTaskIds` 範圍的
 *   兄弟任務也算進去）。
 *
 *   **成本公式（已查證 MS Project 實際行為，非猜測）**：摘要任務可以有自己直接
 *   輸入的 Fixed Cost，且它是**疊加**在子任務彙總之上，不是取代——MS Project 的
 *   `Cost`/`Total Cost` 欄位對任何有子任務的列都是唯讀計算值，等於
 *   `Σ(子任務 Cost) + 自己的 Fixed Cost`；使用者可以直接在摘要列的 Fixed Cost
 *   儲存格輸入值（微軟官方文件不建議這麼做，但欄位本身確實支援，行為是加總不
 *   是覆蓋）。因此本函式對摘要任務採用：
 *
 *       summary.cost      = Σ(已擷取子任務.cost) + summary 自己的直接指派成本 + summary 自己的 fixedCost
 *       summary.fixedCost = Σ(已擷取子任務.fixedCost) + summary 自己的 fixedCost
 *
 *   （「自己的直接指派成本」是本引擎額外處理的邊界情況——MS Project 官方建議
 *   不要對摘要任務指派資源，但 domain 型別沒有禁止，為求資料完整仍納入計算，
 *   語意上與「自己的 fixedCost」對稱：都是「這個任務自己的貢獻」，不是 rollup。）
 *
 *   實作方式：
 *     1. 由葉節點往上遞迴，套用上面的公式。
 *     2. start/finish 取子快照的最早/最晚；durationMinutes 用日曆重新換算兩者間的
 *        工作分鐘數（而不是簡單加總子任務工期，避免平行子任務被重複計入)。
 *     3. 若某個摘要任務在範圍內、但沒有任何子任務被實際擷取（例如 `onlyTaskIds`
 *        只選到摘要本身）：**cost/fixedCost 只計入摘要自己的貢獻（直接指派成本
 *        + 自身 fixedCost），絕不能帶入未擷取子任務的成本**——即使
 *        `computeCost()` 對整個專案算出的摘要 rollup 裡含有那些子任務的貢獻，
 *        這裡也不能借用，否則就是把「沒被選取的兄弟任務」的成本混進來（這正是
 *        上一輪修過的 bug，這裡是同一個錯誤的另一個分支）。start/finish/work/
 *        durationMinutes 不受此限制，直接沿用 Task 目前欄位（結構性排程資訊，
 *        不是成本，沒有重複計算的風險）。
 * - 未排程或手動排程的任務：`Task.start/finish` 型別上一定存在（非 optional），
 *   無論是否已跑過 CPM，都直接快照當下的值，不需要特殊處理。
 */
export function captureBaseline(project: Project, options: SaveBaselineOptions): Baseline {
  const scope = resolveScope(project, options);
  const inScope = (id: string): boolean => scope === null || scope.has(id);

  const costResult = computeCost({ project });
  const taskCostById = new Map(costResult.byTask.map((tc) => [tc.taskId as string, tc]));
  const assignmentCostById = new Map(costResult.byAssignment.map((ac) => [ac.assignmentId as string, ac]));

  const childrenByParentId = new Map<string, Task[]>();
  for (const task of project.tasks) {
    if (task.parentId === null) continue;
    const key = task.parentId as string;
    const list = childrenByParentId.get(key) ?? [];
    list.push(task);
    childrenByParentId.set(key, list);
  }

  // 每個任務「自己」（不含子任務）的直接指派成本總和，供摘要任務彙總時使用。
  const ownAssignmentCostByTaskId = new Map<string, number>();
  for (const assignment of project.assignments) {
    if (!inScope(assignment.taskId as string)) continue;
    const ac = assignmentCostById.get(assignment.id as string);
    const cost = ac !== undefined ? (ac.cost as number) : (assignment.cost as number);
    ownAssignmentCostByTaskId.set(
      assignment.taskId as string,
      (ownAssignmentCostByTaskId.get(assignment.taskId as string) ?? 0) + cost,
    );
  }

  const calendarsById = new Map(project.calendars.map((c) => [c.id as string, c]));
  const resolvedCalendarCache = new Map<string, ResolvedCalendar>();
  const resolveCal = (calendarId: string): ResolvedCalendar => {
    const cached = resolvedCalendarCache.get(calendarId);
    if (cached !== undefined) return cached;
    const cal = calendarsById.get(calendarId);
    if (cal === undefined) throw new EntityNotFoundError('CALENDAR_NOT_FOUND', calendarId);
    const resolved = resolveCalendar(cal, calendarsById);
    resolvedCalendarCache.set(calendarId, resolved);
    return resolved;
  };

  const leafSnapshot = (task: Task): BaselineTaskSnapshot => {
    const taskCost = taskCostById.get(task.id as string);
    return {
      taskId: task.id,
      start: task.start,
      finish: task.finish,
      durationMinutes: task.durationMinutes,
      work: task.work,
      cost: taskCost?.cost ?? task.fixedCost,
      fixedCost: taskCost?.fixedCost ?? task.fixedCost,
    };
  };

  const memo = new Map<string, BaselineTaskSnapshot>();
  function buildSnapshot(task: Task): BaselineTaskSnapshot {
    const cached = memo.get(task.id as string);
    if (cached !== undefined) return cached;

    if (!task.isSummary) {
      const snap = leafSnapshot(task);
      memo.set(task.id as string, snap);
      return snap;
    }

    const children = childrenByParentId.get(task.id as string) ?? [];
    const capturedChildSnaps = children
      .filter((child) => inScope(child.id as string))
      .map((child) => buildSnapshot(child));

    if (capturedChildSnaps.length === 0) {
      // 摘要本身在範圍內，但沒有任何子任務被實際擷取（例如只選了摘要自己）：
      // cost/fixedCost 只能是「摘要自己的」貢獻——絕不可用 computeCost() 對整個
      // 專案算出的 rollup（taskCostById），那會把未擷取子任務的成本也帶進來。
      const ownCost = ownAssignmentCostByTaskId.get(task.id as string) ?? 0;
      const ownFixedCost = task.fixedCost as number;
      const snap: BaselineTaskSnapshot = {
        taskId: task.id,
        start: task.start,
        finish: task.finish,
        durationMinutes: task.durationMinutes,
        work: task.work,
        cost: money(ownCost + ownFixedCost),
        fixedCost: money(ownFixedCost),
      };
      memo.set(task.id as string, snap);
      return snap;
    }

    const work = capturedChildSnaps.reduce((sum, s) => sum + (s.work as number), 0);
    const childrenCost = capturedChildSnaps.reduce((sum, s) => sum + (s.cost as number), 0);
    const childrenFixedCost = capturedChildSnaps.reduce((sum, s) => sum + (s.fixedCost as number), 0);
    const ownCost = ownAssignmentCostByTaskId.get(task.id as string) ?? 0;
    const ownFixedCost = task.fixedCost as number;

    const start = capturedChildSnaps.reduce(
      (min, s) => (s.start < min ? s.start : min),
      capturedChildSnaps[0]?.start ?? task.start,
    );
    const finish = capturedChildSnaps.reduce(
      (max, s) => (s.finish > max ? s.finish : max),
      capturedChildSnaps[0]?.finish ?? task.finish,
    );
    const calendarId = (task.calendarId ?? project.defaultCalendarId) as string;
    const durationMinutes =
      start === finish ? minutes(0) : minutes(Math.abs(workingMinutesBetween(resolveCal(calendarId), start, finish) as number));

    const snap: BaselineTaskSnapshot = {
      taskId: task.id,
      start,
      finish,
      durationMinutes,
      work: minutes(work),
      cost: money(childrenCost + ownCost + ownFixedCost),
      fixedCost: money(childrenFixedCost + ownFixedCost),
    };
    memo.set(task.id as string, snap);
    return snap;
  }

  const tasks: BaselineTaskSnapshot[] = [];
  for (const task of project.tasks) {
    if (!inScope(task.id as string)) continue;
    tasks.push(buildSnapshot(task));
  }

  // `BaselineAssignmentSnapshot` 沒有 durationMinutes 欄位——這是刻意的，不是遺漏。
  // `domain/assignment.ts` 裡 Assignment 本身就沒有獨立的 duration 概念，只有
  // work + start + finish；「期間」純粹是 start/finish 的衍生值。Task 有
  // durationMinutes 是因為那是使用者輸入／排程引擎維護的欄位，兩者不對稱是
  // 領域模型本來的設計，不需要補上重複欄位（那只會製造 durationMinutes 與
  // start/finish 兩個可能發散、缺乏同步機制的真實來源）。若未來某個消費端
  // 真的需要 assignment 的期間，用 calendar-engine 的 `workingMinutesBetween(
  // calendar, start, finish)` 現算即可，不需要儲存。
  const assignments: BaselineAssignmentSnapshot[] = [];
  for (const assignment of project.assignments) {
    if (!inScope(assignment.taskId as string)) continue;
    const assignmentCost = assignmentCostById.get(assignment.id as string);
    assignments.push({
      assignmentId: assignment.id,
      taskId: assignment.taskId,
      start: assignment.start,
      finish: assignment.finish,
      work: assignment.work,
      cost: assignmentCost?.cost ?? assignment.cost,
    });
  }
  const budgetAssignments: BaselineBudgetSnapshot[] = (project.budgetAssignments ?? []).map((assignment) => ({
    budgetAssignmentId: assignment.id, resourceId: assignment.resourceId,
    ...(assignment.budgetWork !== undefined ? { budgetWork: assignment.budgetWork } : {}),
    ...(assignment.budgetCost !== undefined ? { budgetCost: assignment.budgetCost } : {}),
  }));

  return {
    number: options.number,
    name: options.name ?? `Baseline ${options.number}`,
    savedAt: options.savedAt,
    tasks,
    assignments,
    ...(budgetAssignments.length > 0 ? { budgetAssignments } : {}),
  };
}

/** 將基準線寫入專案（覆寫同編號者），回傳新的 Project。 */
export function saveBaseline(project: Project, baseline: Baseline): Project {
  const others = project.baselines.filter((b) => b.number !== baseline.number);
  return { ...project, baselines: [...others, baseline] };
}

/** 清除指定編號的基準線；不影響其他槽位。 */
export function clearBaseline(project: Project, number: BaselineNumber): Project {
  const baselines = project.baselines.filter((b) => b.number !== number);
  if (baselines.length === project.baselines.length) return project;
  return { ...project, baselines };
}
